/**
 * GroupRoundLive - Live Group Round with Multiple Scorecard Types
 * 
 * Supports three scorecard interfaces based on user selection:
 * - QuickCard (voice + tap, group-optimized - NEW)
 * - Voice (existing ScoreEntryForm with voice mode)
 * - Manual (existing ScoreEntryForm with manual mode)
 */

import React, { useState, useEffect, Component } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { eventHandicap, handicapLabel, handicapBaseline, handicapStrokes } from '../utils/handicap';
import { toast } from 'sonner';
import {
  ChevronLeft,
  Users,
  Trophy,
  DollarSign,
  Check,
  Flag,
  AlertCircle,
  Mic,
  Zap,
  PenLine,
  RefreshCw,
  Coins
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import QuickCard from './QuickCard';
import ScoreEntryForm from './ScoreEntryForm';
import UserBubble from './UserBubble';
import soundEffects from '../utils/soundEffects';
import { getUniqueDisplayNames } from '../utils/playerDisplay';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * Error Boundary for GroupRoundLive
 * Catches render errors and displays a recovery UI
 */
class GroupRoundErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('GroupRoundLive Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[var(--ps-bg-base)] flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[var(--ps-text-primary)] mb-2">
              Something went wrong
            </h2>
            <p className="text-[var(--ps-text-muted)] text-sm mb-6">
              The scorecard encountered an error. Your scores have been saved.
            </p>
            <div className="flex flex-col gap-3">
              <Button 
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  window.location.reload();
                }}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Page
              </Button>
              <Button 
                onClick={() => window.location.href = '/play'}
                variant="outline"
                className="w-full"
              >
                Go to Play Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Settlement method options
const SETTLEMENT_METHODS = [
  { id: 'venmo', name: 'Venmo', icon: '💳', color: '#3D95CE' },
  { id: 'zelle', name: 'Zelle', icon: '🏦', color: '#6D1ED4' },
  { id: 'cash', name: 'Cash', icon: '💵', color: '#22c55e' }
];

/**
 * Main GroupRoundLive Component
 */
const GroupRoundLive = ({ user }) => {
  const { roundId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Round data (from navigation state or fetched)
  const [roundData, setRoundData] = useState(location.state?.roundData || null);
  // Scorecard type - prioritize from roundData, then navigation state, default to null (will be fetched)
  const [scorecardType, setScorecardType] = useState(
    location.state?.roundData?.scorecard_type || 
    location.state?.scorecardType || 
    null  // Will be set from fetched data
  );
  const [loading, setLoading] = useState(!roundData);
  const [error, setError] = useState(null);
  const [renderError, setRenderError] = useState(null); // Track render errors
  
  // Score state
  const [scores, setScores] = useState({});
  const [currentHole, setCurrentHole] = useState(1);
  
  // For ScoreEntryForm - track which player is entering scores
  const [activePlayerIndex, setActivePlayerIndex] = useState(0);
  
  // Live sync state
  const [version, setVersion] = useState(0);
  const [lastSyncedBy, setLastSyncedBy] = useState(null);
  const [syncStatus, setSyncStatus] = useState('connected');
  
  // Completion state
  const [isCompleted, setIsCompleted] = useState(false);
  const [results, setResults] = useState([]);
  const [showSettlement, setShowSettlement] = useState(false);
  const [settlements, setSettlements] = useState([]);

  // Fetch round data if not passed via navigation or if scorecard type is missing
  useEffect(() => {
    if (!roundData && roundId) {
      fetchRoundData();
    } else if (roundData) {
      setScores(roundData.scores || {});
      setCurrentHole(roundData.current_hole || 1);
      setVersion(roundData.version || 0);
      setIsCompleted(roundData.status === 'completed');
      // Always use the scorecard type from round data (host's selection)
      if (roundData.scorecard_type) {
        setScorecardType(roundData.scorecard_type);
      }
    }
    
    // If we have roundData but no scorecard type, fetch fresh data
    if (roundData && !roundData.scorecard_type && roundId) {
      fetchRoundData();
    }
  }, [roundId, roundData]);

  // Live sync polling
  useEffect(() => {
    if (!roundId || isCompleted) return;
    
    const getCurrentUserId = () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user.user_id;
      } catch {
        return null;
      }
    };
    
    const currentUserId = getCurrentUserId();
    
    const syncInterval = setInterval(async () => {
      try {
        setSyncStatus('syncing');
        const response = await axios.get(`${API}/group-rounds/${roundId}/sync`, {
          params: { version }
        });
        
        if (response.data.changed) {
          const prevScores = JSON.stringify(scores);
          const newScores = JSON.stringify(response.data.scores);
          
          setScores(response.data.scores);
          setCurrentHole(response.data.current_hole);
          setVersion(response.data.version);
          setLastSyncedBy(response.data.last_updated_by);
          
          if (response.data.settlements) {
            setSettlements(response.data.settlements);
          }
          
          if (response.data.last_updated_by && 
              response.data.last_updated_by !== currentUserId && 
              prevScores !== newScores) {
            const updater = roundData?.players?.find(p => p.player_id === response.data.last_updated_by);
            if (updater) {
              toast.info(`${updater.name} updated scores`, {
                duration: 2000,
                icon: '🔄'
              });
            }
          }
          
          if (response.data.status === 'completed' && !isCompleted) {
            setIsCompleted(true);
            setResults(response.data.results || []);
            soundEffects.playSuccess();
            toast.success('Round completed!');
            setShowSettlement(true);
          }
        }
        
        setSyncStatus('connected');
      } catch (err) {
        console.error('Sync error:', err);
        setSyncStatus('offline');
      }
    }, 3000);
    
    return () => clearInterval(syncInterval);
  }, [roundId, version, isCompleted, scores, roundData]);

  const fetchRoundData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/group-rounds/${roundId}`);
      setRoundData(response.data);
      setScores(response.data.scores || {});
      setCurrentHole(response.data.current_hole || 1);
      setVersion(response.data.version || 0);
      setIsCompleted(response.data.status === 'completed');
      if (response.data.scorecard_type) {
        setScorecardType(response.data.scorecard_type);
      }
      if (response.data.results) {
        setResults(response.data.results);
      }
      if (response.data.settlements) {
        setSettlements(response.data.settlements);
      }
    } catch (err) {
      console.error('Error fetching round:', err);
      setError(err.response?.data?.detail || 'Failed to load round');
      toast.error('Failed to load round data');
    } finally {
      setLoading(false);
    }
  };

  // Handle score updates from QuickCard
  const handleScoresUpdate = async (newScores) => {
    setScores(newScores);
    
    // Save to backend
    try {
      await axios.put(`${API}/group-rounds/${roundId}/scores`, {
        scores: newScores
      });
    } catch (err) {
      console.error('Error saving scores:', err);
    }
  };

  // Handle hole completion from QuickCard
  const handleHoleComplete = async (holeNumber, holeScores) => {
    try {
      const response = await axios.put(`${API}/group-rounds/${roundId}/scores`, {
        hole_number: holeNumber,
        scores: holeScores
      });
      setCurrentHole(holeNumber + 1);
      if (response.data.version) {
        setVersion(response.data.version);
      }
      if (response.data.scores) {
        setScores(response.data.scores);
      }
    } catch (err) {
      console.error('Error saving scores:', err);
      toast.error('Failed to save scores');
    }
  };

  // Handle scores from ScoreEntryForm (for manual/voice modes)
  const handleScoreEntryFormChange = (scoresArray) => {
    // Convert array to the format expected by backend
    const activePlayer = players[activePlayerIndex];
    if (!activePlayer) return;
    
    // Convert array to hole-keyed object
    const playerScores = {};
    scoresArray.forEach((score, index) => {
      if (score && score !== '') {
        const holeNum = index + 1;
        playerScores[holeNum] = parseInt(score);
      }
    });
    
    const newScores = {
      ...scores,
      [activePlayer.player_id]: playerScores
    };
    
    setScores(newScores);
  };

  // Handle save from ScoreEntryForm
  const handleScoreEntryFormSave = async (data) => {
    try {
      const activePlayer = players[activePlayerIndex];
      if (!activePlayer) return;
      
      // Update scores for this player
      const playerScores = {};
      data.scores.forEach((score, index) => {
        if (score && score !== '') {
          const holeNum = index + 1;
          playerScores[holeNum] = parseInt(score);
        }
      });
      
      const newScores = {
        ...scores,
        [activePlayer.player_id]: playerScores
      };
      
      await axios.put(`${API}/group-rounds/${roundId}/scores`, {
        scores: newScores
      });
      
      setScores(newScores);
      
      // Move to next player if not the last one
      if (activePlayerIndex < players.length - 1) {
        const nextPlayer = players[activePlayerIndex + 1];
        setActivePlayerIndex(activePlayerIndex + 1);
        toast.success(`${activePlayer?.name || 'Player'}'s scores saved. Now entering scores for ${nextPlayer?.name || 'next player'}`);
      } else {
        toast.success('All scores saved!');
      }
    } catch (err) {
      console.error('Error saving scores:', err);
      toast.error('Failed to save scores');
    }
  };

  // Complete the round
  const handleCompleteRound = async () => {
    try {
      const response = await axios.post(`${API}/group-rounds/${roundId}/complete`);
      setIsCompleted(true);
      setResults(response.data.results || []);
      soundEffects.playSuccess();
      toast.success('Round completed! Great game!');
      setShowSettlement(true);
    } catch (err) {
      console.error('Error completing round:', err);
      toast.error(err.response?.data?.detail || 'Failed to complete round');
    }
  };

  // Record a settlement
  const handleRecordSettlement = async (fromPlayerId, toPlayerId, amount, method) => {
    try {
      await axios.post(`${API}/group-rounds/${roundId}/settlements`, {
        round_id: roundId,
        from_player_id: fromPlayerId,
        to_player_id: toPlayerId,
        amount,
        method
      });
      toast.success(`Settlement recorded: ${method}`);
      await fetchRoundData();
    } catch (err) {
      console.error('Error recording settlement:', err);
      toast.error('Failed to record settlement');
    }
  };

  // Go back
  const handleBack = () => {
    if (isCompleted) {
      navigate('/history');
    } else {
      if (window.confirm('Leave this round? Your scores have been saved.')) {
        navigate('/history');
      }
    }
  };

  // Get scorecard type icon
  const getScorecardIcon = () => {
    switch (scorecardType) {
      case 'voice': return <Mic className="w-4 h-4 text-blue-400" />;
      case 'manual': return <PenLine className="w-4 h-4 text-amber-400" />;
      default: return <Zap className="w-4 h-4 text-green-400" />;
    }
  };

  const getScorecardLabel = () => {
    switch (scorecardType) {
      case 'voice': return 'Voice';
      case 'manual': return 'Manual';
      default: return 'QuickCard';
    }
  };

  // Convert player scores to array format for ScoreEntryForm
  const getPlayerScoresArray = (playerId) => {
    const playerScores = scores[playerId] || {};
    const arr = Array(18).fill('');
    
    // Handle both object format { "1": 4 } and array format [4, 5, ...]
    if (Array.isArray(playerScores)) {
      playerScores.forEach((score, idx) => {
        if (idx < 18 && score !== null && score !== undefined) {
          arr[idx] = score.toString();
        }
      });
    } else {
      Object.entries(playerScores).forEach(([holeNum, score]) => {
        const idx = parseInt(holeNum) - 1;
        if (idx >= 0 && idx < 18 && score !== null && score !== undefined) {
          arr[idx] = score.toString();
        }
      });
    }
    return arr;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--ps-bg-base)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--ps-text-muted)]">Loading round...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--ps-bg-base)] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-[var(--ps-text-primary)] mb-2">Error loading round</p>
          <p className="text-[var(--ps-text-muted)] text-sm mb-4">{error}</p>
          <Button onClick={() => navigate('/history')} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (!roundData) {
    return (
      <div className="min-h-screen bg-[var(--ps-bg-base)] flex items-center justify-center">
        <p className="text-[var(--ps-text-muted)]">No round data found</p>
      </div>
    );
  }

  // Determine the scorecard type to use (from round data or default to manual if still loading)
  const effectiveScorecardType = scorecardType || roundData?.scorecard_type || 'manual';

  const players = roundData.players?.map(p => ({
    player_id: p.player_id,
    name: p.name || 'Player',
    handicap: eventHandicap(p),
    is_host: p.is_host || false
  })) || [];

  // Generate unique display names for players with duplicate first names
  const displayNames = getUniqueDisplayNames(players);

  // Determine if current user is the host (only host can edit scores)
  const currentUserId = user?.user_id;
  const hostPlayer = players.find(p => p.is_host);
  const isHost = hostPlayer?.player_id === currentUserId;
  const currentPlayer = players.find(p => p.player_id === currentUserId);
  
  // Debug: Log host detection (can be removed later)
  console.log('Host detection:', { currentUserId, hostPlayerId: hostPlayer?.player_id, isHost, playerCount: players.length });

  // Safety: ensure activePlayerIndex is valid
  const safePlayerIndex = Math.min(activePlayerIndex, Math.max(0, players.length - 1));
  const activePlayer = players.length > 0 ? players[safePlayerIndex] : { player_id: '', name: 'Player', handicap: null };

  // If players array is empty after data loaded, show error
  if (!loading && players.length === 0) {
    return (
      <div className="min-h-screen bg-[var(--ps-bg-base)] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-[var(--ps-text-primary)] mb-2">No players found</p>
          <p className="text-[var(--ps-text-muted)] text-sm mb-4">This round has no players configured</p>
          <Button onClick={() => navigate('/history')} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--ps-bg-base)]">
      {/* Header */}
      <div className="sticky top-0 bg-[var(--ps-bg-base)]/95 backdrop-blur border-b border-[var(--ps-border)] z-50">
        <div className="flex items-center justify-between p-4">
          <button onClick={handleBack} className="flex items-center gap-2 text-[var(--ps-text-muted)] hover:text-[var(--ps-text-primary)]">
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          <div className="text-center">
            <h1 className="text-[var(--ps-text-primary)] font-bold flex items-center justify-center gap-2">
              <Users className="w-4 h-4 text-blue-400" />
              Group Round
              <span 
                className={`w-2 h-2 rounded-full ${
                  syncStatus === 'connected' ? 'bg-green-500' : 
                  syncStatus === 'syncing' ? 'bg-yellow-500 animate-pulse' : 
                  'bg-red-500'
                }`}
                title={`Sync: ${syncStatus}`}
              />
            </h1>
            <p className="text-xs text-[var(--ps-text-muted)] flex items-center justify-center gap-1">
              {getScorecardIcon()}
              {getScorecardLabel()} • {roundData.course_name}
            </p>
          </div>
          <div className="w-16 text-right">
            <span className="text-xs text-[var(--ps-text-muted)]">
              {players.length} players
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {!isCompleted ? (
        <>
          {/* Non-host players see VIEW-ONLY scorecard */}
          {!isHost && (
            <div className="p-4">
              {/* View-only indicator */}
              <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                <div className="flex items-center gap-2 text-blue-400">
                  <Users className="w-5 h-5" />
                  <span className="font-medium">Live Scorecard</span>
                </div>
                <p className="text-sm text-[var(--ps-text-muted)] mt-1">
                  {hostPlayer?.name || 'Host'} is managing scores. Watch the action live!
                </p>
              </div>
              
              {/* Compact Live Scorecard Table - Same format as host view bottom section */}
              <div className="mt-4">
                <div className="overflow-x-auto rounded-xl border border-[var(--ps-border)] bg-[var(--ps-bg-surface)]">
                  <table className="w-full text-sm">
                    <thead>
                      {/* Hole numbers row */}
                      <tr className="bg-[var(--ps-bg-elevated)]">
                        <th className="sticky left-0 bg-[var(--ps-bg-elevated)] px-2 py-2 text-left font-bold text-[var(--ps-text-primary)] min-w-[80px] border-r border-[var(--ps-border)]">
                          Hole
                        </th>
                        {Array.from({ length: roundData.holes || 9 }, (_, i) => {
                          const holeNum = roundData.starting_nine === 'front' ? i + 1 : i + 10;
                          return (
                            <th 
                              key={i} 
                              className={`px-1 py-2 text-center font-bold min-w-[32px] ${
                                holeNum === (roundData.current_hole || 1) 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'text-[var(--ps-text-muted)]'
                              }`}
                            >
                              {holeNum}
                            </th>
                          );
                        })}
                        <th className="px-2 py-2 text-center font-bold text-[var(--ps-text-primary)] min-w-[40px] border-l border-[var(--ps-border)]">
                          {roundData.starting_nine === 'back' || roundData.holes === 18 ? 'IN' : 'OUT'}
                        </th>
                      </tr>
                      
                      {/* Par row */}
                      <tr className="bg-[var(--ps-bg-elevated)] border-b border-[var(--ps-border)]">
                        <td className="sticky left-0 bg-[var(--ps-bg-elevated)] px-2 py-1 text-[var(--ps-text-muted)] font-medium border-r border-[var(--ps-border)]">
                          Par
                        </td>
                        {Array.from({ length: roundData.holes || 9 }, (_, i) => (
                          <td key={i} className="px-1 py-1 text-center text-[var(--ps-text-muted)]">
                            {roundData.hole_pars?.[i] || 4}
                          </td>
                        ))}
                        <td className="px-2 py-1 text-center text-[var(--ps-text-muted)] font-medium border-l border-[var(--ps-border)]">
                          {Array.from({ length: roundData.holes || 9 }, (_, i) => roundData.hole_pars?.[i] || 4).reduce((a, b) => a + b, 0)}
                        </td>
                      </tr>
                      
                      {/* Handicap row */}
                      <tr className="border-b border-[var(--ps-border)]">
                        <td className="sticky left-0 bg-[var(--ps-bg-surface)] px-2 py-1 text-[var(--ps-text-muted)] text-xs border-r border-[var(--ps-border)]">
                          Hdcp
                        </td>
                        {Array.from({ length: roundData.holes || 9 }, (_, i) => (
                          <td key={i} className="px-1 py-1 text-center text-[var(--ps-text-muted)] text-xs">
                            {roundData.hole_handicaps?.[i] || i + 1}
                          </td>
                        ))}
                        <td className="border-l border-[var(--ps-border)]"></td>
                      </tr>
                    </thead>
                    
                    <tbody>
                      {players.map((player, pIdx) => {
                        const playerScores = scores[player.player_id] || [];
                        const totalScore = Array.isArray(playerScores) 
                          ? playerScores.filter(s => s !== null && s !== undefined).reduce((a, b) => a + b, 0)
                          : Object.values(playerScores).filter(s => s !== null && s !== undefined).reduce((a, b) => a + b, 0);
                        const playerDisplayName = displayNames[player.player_id] || player.name.split(' ')[0];
                        const isCurrentUser = player.player_id === currentUserId;
                        
                        // Get score style based on par comparison
                        const getScoreStyle = (score, par) => {
                          if (score === null || score === undefined) return 'border-[var(--ps-border)] bg-[var(--ps-bg-surface)] text-[var(--ps-text-muted)]';
                          if (score < par) return 'border-green-500 bg-green-500/30 text-green-300'; // Birdie or better
                          if (score === par) return 'border-green-400/50 bg-green-500/10 text-green-200'; // Par
                          if (score === par + 1) return 'border-amber-500 bg-amber-500/30 text-amber-200'; // Bogey
                          return 'border-red-500 bg-red-500/30 text-red-200'; // Double+
                        };
                        
                        return (
                          <tr 
                            key={player.player_id} 
                            className={`${
                              isCurrentUser 
                                ? 'bg-green-500/10' 
                                : pIdx % 2 === 0 ? 'bg-[var(--ps-bg-surface)]' : 'bg-[var(--ps-bg-elevated)]'
                            }`}
                          >
                            {/* Player name + handicap */}
                            <td className={`sticky left-0 ${
                              isCurrentUser 
                                ? 'bg-green-500/10' 
                                : pIdx % 2 === 0 ? 'bg-[var(--ps-bg-surface)]' : 'bg-[var(--ps-bg-elevated)]'
                            } px-2 py-2 border-r border-[var(--ps-border)]`}>
                              <div className="flex items-center gap-2">
                                <div>
                                  <div className="font-medium text-[var(--ps-text-primary)] truncate max-w-[60px] flex items-center gap-1">
                                    {playerDisplayName}
                                    {player.is_host && <span className="text-[10px] text-green-400">(H)</span>}
                                    {isCurrentUser && <span className="text-[10px] text-blue-400">(You)</span>}
                                  </div>
                                  <div className="text-[10px] text-[var(--ps-text-muted)]">
                                    {handicapLabel(eventHandicap(player))}
                                  </div>
                                </div>
                              </div>
                            </td>
                            
                            {/* Scores */}
                            {Array.from({ length: roundData.holes || 9 }, (_, hIdx) => {
                              const score = Array.isArray(playerScores) ? playerScores[hIdx] : playerScores[hIdx + 1];
                              const par = roundData.hole_pars?.[hIdx] || 4;
                              const holeNum = roundData.starting_nine === 'front' ? hIdx + 1 : hIdx + 10;
                              const isCurrentHole = holeNum === (roundData.current_hole || 1);
                              
                              // Check if player gets stroke on this hole
                              const holeHdcp = roundData.hole_handicaps?.[hIdx] || hIdx + 1;
                              const minHandicap = handicapBaseline(players);
                              const adjustedHdcp = handicapStrokes(eventHandicap(player), minHandicap);
                              const getsStroke = adjustedHdcp >= holeHdcp;
                              
                              return (
                                <td 
                                  key={hIdx} 
                                  className={`px-1 py-1 text-center ${
                                    isCurrentHole ? 'bg-green-500/10' : ''
                                  }`}
                                >
                                  <div className={`relative w-7 h-7 mx-auto rounded flex items-center justify-center text-xs font-bold border ${getScoreStyle(score, par)}`}>
                                    {score !== null && score !== undefined ? score : '-'}
                                    {getsStroke && (
                                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                            
                            {/* Total */}
                            <td className="px-2 py-1 text-center font-bold text-[var(--ps-text-primary)] border-l border-[var(--ps-border)]">
                              {totalScore || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Legend */}
                <div className="mt-3 px-2 text-[10px] text-[var(--ps-text-muted)] flex flex-wrap gap-3 justify-center">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-green-500/30 border border-green-500 rounded" /> Birdie
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-green-500/10 border border-green-400/50 rounded" /> Par
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-amber-500/30 border border-amber-500 rounded" /> Bogey
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-blue-500 rounded-full" /> Stroke
                  </span>
                </div>
              </div>
              
              {/* Sync Status */}
              <div className="mt-4 text-center">
                <p className="text-xs text-[var(--ps-text-muted)] flex items-center justify-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${syncStatus === 'connected' ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
                  {syncStatus === 'connected' ? 'Live updates active' : 'Reconnecting...'}
                </p>
              </div>
            </div>
          )}
          
          {/* HOST-ONLY: Full editing controls */}
          {isHost && (
            <>
              {/* QuickCard mode - group-optimized UI */}
              {effectiveScorecardType === 'quickcard' && (
            <QuickCard
              useHandicaps={roundData.use_handicaps === true}
              players={players}
              holes={roundData.holes || 9}
              startingNine={roundData.starting_nine || 'front'}
              holePars={roundData.hole_pars || []}
              holeHandicaps={roundData.hole_handicaps || []}
              courseName={roundData.course_name}
              eventType="group"
              skinValue={0}
              onScoresUpdate={handleScoresUpdate}
              onHoleComplete={handleHoleComplete}
              initialScores={scores}
              initialHole={roundData.current_hole || null}
              enableVoiceReadback={true}
            />
          )}
          
          {/* Voice mode - existing ScoreEntryForm with voice enabled */}
          {effectiveScorecardType === 'voice' && (
            <div className="p-4">
              {/* Player selector for group rounds */}
              {players.length > 1 && (
                <div className="mb-4">
                  <p className="text-sm text-[var(--ps-text-muted)] mb-2">Entering scores for:</p>
                  <div className="flex flex-wrap gap-2">
                    {players.map((player, idx) => {
                      const playerDisplayName = displayNames[player.player_id] || player.name.split(' ')[0];
                      return (
                        <button
                          key={player.player_id}
                          onClick={() => setActivePlayerIndex(idx)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all ${
                            idx === activePlayerIndex
                              ? 'bg-green-500 text-white'
                              : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] border border-[var(--ps-border)]'
                          }`}
                        >
                          <UserBubble name={player.name} size="xs" showName={false} />
                          <span className="text-sm font-medium">{playerDisplayName}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <ScoreEntryForm
                courseId={roundData.course_id}
                numberOfHoles={roundData.holes || 18}
                initialScores={getPlayerScoresArray(activePlayer?.player_id)}
                onScoresChange={handleScoreEntryFormChange}
                onSave={handleScoreEntryFormSave}
                showHolesSelector={false}
                showScanOption={true}
                showVoiceOption={true}
                playerName={displayNames[activePlayer?.player_id] || activePlayer?.name}
                submitButtonText={activePlayerIndex < players.length - 1 ? `Save & Next Player` : "Save Score"}
                startingNine={roundData.starting_nine || 'front'}
                holePars={roundData.hole_pars || []}
                eventHoles={roundData.holes}
              />
            </div>
          )}
          
          {/* Manual mode - existing ScoreEntryForm in manual mode */}
          {effectiveScorecardType === 'manual' && (
            <div className="p-4">
              {/* Player selector for group rounds */}
              {players.length > 1 && (
                <div className="mb-4">
                  <p className="text-sm text-[var(--ps-text-muted)] mb-2">Entering scores for:</p>
                  <div className="flex flex-wrap gap-2">
                    {players.map((player, idx) => {
                      const playerDisplayName = displayNames[player.player_id] || player.name.split(' ')[0];
                      return (
                        <button
                          key={player.player_id}
                          onClick={() => setActivePlayerIndex(idx)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all ${
                            idx === activePlayerIndex
                              ? 'bg-green-500 text-white'
                              : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] border border-[var(--ps-border)]'
                          }`}
                        >
                          <UserBubble name={player.name} size="xs" showName={false} />
                          <span className="text-sm font-medium">{playerDisplayName}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <ScoreEntryForm
                courseId={roundData.course_id}
                numberOfHoles={roundData.holes || 18}
                initialScores={getPlayerScoresArray(activePlayer?.player_id)}
                onScoresChange={handleScoreEntryFormChange}
                onSave={handleScoreEntryFormSave}
                showHolesSelector={false}
                showScanOption={true}
                showVoiceOption={true}
                playerName={displayNames[activePlayer?.player_id] || activePlayer?.name}
                submitButtonText={activePlayerIndex < players.length - 1 ? `Save & Next Player` : "Save Score"}
                startingNine={roundData.starting_nine || 'front'}
                holePars={roundData.hole_pars || []}
                eventHoles={roundData.holes}
              />
            </div>
          )}
            </>
          )}
        </>
      ) : (
        /* Completed Round - Results */
        <div className="p-4 space-y-6">
          <div className="text-center py-6">
            <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[var(--ps-text-primary)] mb-2">Round Complete!</h2>
            <p className="text-[var(--ps-text-muted)]">{roundData.course_name}</p>
          </div>

          <div className="bg-[var(--ps-bg-elevated)] rounded-xl border border-[var(--ps-border)] overflow-hidden">
            <div className="p-4 border-b border-[var(--ps-border)]">
              <h3 className="font-bold text-[var(--ps-text-primary)] flex items-center gap-2">
                <Flag className="w-4 h-4 text-green-400" />
                Final Standings
              </h3>
            </div>
            <div className="divide-y divide-[var(--ps-border)]">
              {results.map((result, index) => {
                const resultDisplayName = displayNames[result.player_id] || result.name;
                return (
                  <div 
                    key={result.player_id}
                    className={`flex items-center justify-between p-4 ${
                      index === 0 ? 'bg-yellow-500/10' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs ${
                        index === 0 ? 'bg-yellow-500 text-black' :
                        index === 1 ? 'bg-gray-400 text-black' :
                        index === 2 ? 'bg-amber-700 text-white' :
                        'bg-[var(--ps-bg-hover)] text-[var(--ps-text-muted)]'
                      }`}>
                        {index + 1}
                      </div>
                      <UserBubble
                        userId={result.user_id}
                        picture={result.picture}
                        name={result.player_name}
                        bubbleColor={index === 0 ? 'yellow' : 'green'}
                        size="sm"
                        clickable={!!result.user_id}
                      />
                      <div>
                        <p className="text-[var(--ps-text-primary)] font-medium">{resultDisplayName}</p>
                        <p data-testid={`group-result-handicap-${result.player_id}`} className="text-xs text-[var(--ps-text-muted)]">HDCP: {handicapLabel(result.handicap)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[var(--ps-text-primary)]">{result.total_score}</p>
                      <p className={`text-xs ${
                        result.vs_par === 0 ? 'text-[var(--ps-text-muted)]' :
                        result.vs_par > 0 ? 'text-red-400' : 'text-green-400'
                      }`}>
                        {result.vs_par === 0 ? 'E' : result.vs_par > 0 ? `+${result.vs_par}` : result.vs_par}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {roundData.entry_fee > 0 && (
            <div className={`${roundData.entry_fee_type === 'coins' ? 'bg-amber-900/20 border-amber-800' : 'bg-green-900/20 border-green-800'} border rounded-xl p-4`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {roundData.entry_fee_type === 'coins' ? (
                    <Coins className="w-5 h-5 text-amber-400" />
                  ) : (
                    <DollarSign className="w-5 h-5 text-green-400" />
                  )}
                  <span className={`${roundData.entry_fee_type === 'coins' ? 'text-amber-400' : 'text-green-400'} font-medium`}>Total Pot</span>
                </div>
                <span className={`text-2xl font-bold ${roundData.entry_fee_type === 'coins' ? 'text-amber-400' : 'text-green-400'}`}>
                  {roundData.entry_fee_type === 'coins' ? '' : '$'}{roundData.pot_total || roundData.entry_fee * players.length}{roundData.entry_fee_type === 'coins' ? ' coins' : ''}
                </span>
              </div>
              <p className={`text-sm ${roundData.entry_fee_type === 'coins' ? 'text-amber-400/70' : 'text-green-400/70'} mt-2`}>
                Winner: {displayNames[results[0]?.player_id] || results[0]?.name} takes the pot!
              </p>
            </div>
          )}

          <Button
            onClick={() => setShowSettlement(true)}
            className={`w-full py-6 ${roundData.entry_fee_type === 'coins' ? 'bg-amber-600 hover:bg-amber-500' : 'bg-blue-600 hover:bg-blue-500'}`}
          >
            {roundData.entry_fee_type === 'coins' ? (
              <Coins className="w-5 h-5 mr-2" />
            ) : (
              <DollarSign className="w-5 h-5 mr-2" />
            )}
            {roundData.entry_fee_type === 'coins' ? 'Distribute Coins' : 'Settle Up'}
          </Button>

          <Button
            onClick={() => navigate('/history')}
            variant="outline"
            className="w-full border-[var(--ps-border)]"
          >
            Done
          </Button>
        </div>
      )}

      {/* Complete Round Button - Only show when all scores are entered */}
      {!isCompleted && effectiveScorecardType === 'quickcard' && isHost && (() => {
        // Check if all players have scores for all holes
        const allScoresEntered = players.length > 0 && players.every(player => {
          const playerScores = scores[player.player_id];
          if (!playerScores) return false;
          
          // Check if this player has scores for all holes
          const totalHoles = roundData.holes || 18;
          if (Array.isArray(playerScores)) {
            const validScores = playerScores.filter(s => s !== null && s !== undefined);
            return validScores.length >= totalHoles;
          } else if (typeof playerScores === 'object') {
            return Object.keys(playerScores).length >= totalHoles;
          }
          return false;
        });
        
        // Only render button if all scores are entered
        if (!allScoresEntered) return null;
        
        return (
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[var(--ps-bg-base)] to-transparent">
            <Button
              onClick={handleCompleteRound}
              className="w-full bg-green-600 hover:bg-green-500 py-6"
            >
              <Check className="w-5 h-5 mr-2" />
              Complete Round
            </Button>
          </div>
        );
      })()}

      {/* Settlement Modal */}
      <Dialog open={showSettlement} onOpenChange={setShowSettlement}>
        <DialogContent className="max-w-md bg-[var(--ps-bg-surface)] border-[var(--ps-border)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--ps-text-primary)] flex items-center gap-2">
              {roundData?.entry_fee_type === 'coins' ? (
                <Coins className="w-5 h-5 text-amber-400" />
              ) : (
                <DollarSign className="w-5 h-5 text-green-400" />
              )}
              {roundData?.entry_fee_type === 'coins' ? 'Coin Distribution' : 'Settle Up'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {roundData.entry_fee > 0 ? (
              roundData.entry_fee_type === 'coins' ? (
                // COINS - Automatic distribution
                <>
                  <div className="bg-amber-900/20 border border-amber-500/30 rounded-xl p-4 text-center">
                    <Coins className="w-12 h-12 text-amber-400 mx-auto mb-2" />
                    <p className="text-amber-400 font-bold text-xl">
                      {roundData.pot_total || roundData.entry_fee * players.length} Coins
                    </p>
                    <p className="text-amber-400/70 text-sm mt-1">Total Prize Pool</p>
                  </div>
                  
                  <div className="bg-[var(--ps-bg-elevated)] rounded-lg p-4 space-y-3">
                    <p className="text-[var(--ps-text-primary)] font-medium text-center">
                      🏆 Winner: {displayNames[results[0]?.player_id] || results[0]?.name}
                    </p>
                    <p className="text-[var(--ps-text-muted)] text-sm text-center">
                      Coins have been automatically distributed to the winner's balance!
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-center gap-2 text-emerald-400 text-sm">
                    <Check className="w-4 h-4" />
                    <span>No manual settlement needed for coin games</span>
                  </div>
                </>
              ) : (
                // WALLET/USD - Manual settlement options
                <>
                  <p className="text-[var(--ps-text-muted)] text-sm">
                    {displayNames[results[0]?.player_id] || results[0]?.name} won ${roundData.pot_total}. How would they like to receive payment?
                  </p>
                  
                  <div className="space-y-2">
                    {SETTLEMENT_METHODS.map((method) => (
                      <button
                        key={method.id}
                        onClick={() => {
                          results.slice(1).forEach(loser => {
                            handleRecordSettlement(
                              loser.player_id,
                              results[0].player_id,
                              roundData.entry_fee,
                              method.id
                            );
                          });
                          setShowSettlement(false);
                        }}
                        className="w-full flex items-center gap-4 p-4 bg-[var(--ps-bg-elevated)] rounded-lg border border-[var(--ps-border)] hover:border-[var(--ps-text-muted)] transition-colors"
                      >
                        <span className="text-2xl">{method.icon}</span>
                        <div className="flex-1 text-left">
                          <p className="text-[var(--ps-text-primary)] font-medium">{method.name}</p>
                          <p className="text-xs text-[var(--ps-text-muted)]">Settled outside app</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  <p className="text-xs text-amber-500/80 text-center flex items-center justify-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Stripe auto-pay coming soon!
                  </p>
                </>
              )
            ) : (
              <div className="text-center py-8">
                <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <p className="text-[var(--ps-text-primary)]">No entry fee for this round</p>
                <p className="text-[var(--ps-text-muted)] text-sm mt-2">Great game everyone!</p>
              </div>
            )}
          </div>
          
          <Button
            onClick={() => setShowSettlement(false)}
            variant="outline"
            className="w-full border-[var(--ps-border)]"
          >
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Wrap with Error Boundary for crash protection
const GroupRoundLiveWithErrorBoundary = (props) => (
  <GroupRoundErrorBoundary>
    <GroupRoundLive {...props} />
  </GroupRoundErrorBoundary>
);

export default GroupRoundLiveWithErrorBoundary;
